/**
 * SolHtmlInline · renderiza HTML sanitizado de Sol con intercepción de clicks
 * en rutas internas (data-sol-internal="true") para usar React Router en lugar
 * de full reload.
 *
 * Reemplazo de `<span dangerouslySetInnerHTML={{ __html: renderSolHtmlSafe(text) }} />`
 * cuando necesitás soporte para links internos `/...`.
 *
 * Anexo C · Fase C7 · 2026-05-28.
 */
import { useCallback, useRef, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { renderSolHtmlSafe } from '@/lib/sol-html';

/**
 * Clases responsive para el contenido HTML de Sol dentro de una burbuja.
 *
 * Un bloque `<pre>` (mensaje para copiar-pegar) NO debe forzar scroll horizontal
 * en la burbuja (incómodo en web y mobile). Acá:
 * - `min-w-0` + `break-words` → la burbuja puede achicarse y parte palabras/URLs largas.
 * - `<pre>` → `whitespace-pre-wrap` + `break-words` → el texto SE ENVUELVE dentro
 *   del recuadro (sin scroll horizontal), preservando los saltos de línea.
 * - `<pre>` es `relative` con `pr-9` → deja lugar arriba a la derecha para el
 *   botón de copiar que inyecta SolHtmlInline.
 * - `<pre> <code>` → resetea el chip inline (sin doble fondo/borde dentro del bloque).
 *
 * El tamaño del `<code>` inline lo define cada burbuja (difiere por densidad).
 */
export const SOL_CONTENT_RESPONSIVE =
  'min-w-0 break-words [overflow-wrap:anywhere] ' +
  '[&_pre]:relative [&_pre]:my-1.5 [&_pre]:max-w-full [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_pre]:[overflow-wrap:anywhere] ' +
  '[&_pre]:rounded-md [&_pre]:border [&_pre]:border-border-dim [&_pre]:bg-muted ' +
  '[&_pre]:p-2.5 [&_pre]:pr-9 [&_pre]:font-mono [&_pre]:text-caption [&_pre]:leading-relaxed ' +
  '[&_pre_code]:border-0 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit';

// Iconos estilo lucide (el clásico "doble cuadrado" de copiar + el check de OK).
const COPY_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
const CHECK_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

const COPY_BTN_CLASS =
  'sol-copy-btn absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center ' +
  'rounded border border-border-dim bg-background/80 text-fg-dim ' +
  'hover:bg-background hover:text-fg transition-colors';

interface Props {
  /** Texto crudo que devuelve Sol (puede incluir el subset HTML permitido). */
  text: string;
  /** Tag wrapper. Default 'div'. */
  as?: 'div' | 'span' | 'p';
  className?: string;
}

export function SolHtmlInline({ text, as = 'div', className }: Props): ReactNode {
  const ref = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();

  const handleClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('a[data-sol-internal="true"]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/')) return;
      // Solo interceptamos clicks "normales". Cmd/Ctrl/click middle abren en pestaña nueva.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      navigate(href);
    },
    [navigate],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener('click', handleClick);
    return () => el.removeEventListener('click', handleClick);
  }, [handleClick]);

  const html = renderSolHtmlSafe(text);

  // Botón "copiar" en cada bloque <pre> (los mensajes/copy que Sol arma para
  // copiar-pegar y mandar a un cliente). Inyectamos sobre el DOM ya renderizado.
  // React reescribe el innerHTML cuando cambia el contenido (ej. mientras el
  // mensaje se va completando) y borra los botones, así que usamos un
  // MutationObserver para re-inyectar ante cualquier cambio del subárbol. El
  // guard `dataSolCopy` evita duplicados y loops por nuestra propia mutación.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const listeners: Array<{ btn: HTMLButtonElement; fn: (e: Event) => void; timer: () => number }> = [];

    const inject = () => {
      const pres = Array.from(el.querySelectorAll('pre')) as HTMLPreElement[];
      for (const pre of pres) {
        if (pre.dataset.solCopy === '1') continue;
        pre.dataset.solCopy = '1';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.title = 'Copiar';
        btn.setAttribute('aria-label', 'Copiar');
        btn.className = COPY_BTN_CLASS;
        btn.innerHTML = COPY_ICON;
        let resetTimer = 0;
        const onClick = (event: Event) => {
          event.preventDefault();
          event.stopPropagation();
          // Leemos el texto al click (sin el botón, que no aporta texto).
          const text = pre.innerText;
          const done = () => {
            btn.innerHTML = CHECK_ICON;
            btn.classList.add('text-[var(--ok)]');
            window.clearTimeout(resetTimer);
            resetTimer = window.setTimeout(() => {
              btn.innerHTML = COPY_ICON;
              btn.classList.remove('text-[var(--ok)]');
            }, 1400);
          };
          if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).then(done).catch(() => done());
          } else {
            done();
          }
        };
        btn.addEventListener('click', onClick);
        pre.appendChild(btn);
        listeners.push({ btn, fn: onClick, timer: () => resetTimer });
      }
    };

    inject();
    const observer = new MutationObserver(() => inject());
    observer.observe(el, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      for (const l of listeners) {
        window.clearTimeout(l.timer());
        l.btn.removeEventListener('click', l.fn);
      }
    };
  }, []);
  const Tag = as as 'div';

  return (
    <Tag
      ref={ref as unknown as React.Ref<HTMLDivElement>}
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
